// @flow
import { Field, Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import { TextField } from "formik-material-ui";
import { Button, LinearProgress } from "@material-ui/core";
import { useAuth, useFirestore, useFirestoreCollectionData } from "reactfire";
import { v4 as uuid } from "uuid";
import { useSnackbar } from "notistack";
import _ from "lodash";

const companionSchema = Yup.object().shape({
  name: Yup.string().min(2, "Benötigte Angabe").required("Benötigte Angabe"),
  phoneNumber: Yup.string()
    .min(5, "Benötigte Angabe")
    .max(15, "Zu lange Nummer")
    .required("Benötigte Angabe"),
  streetAddress: Yup.string()
    .min(2, "Benötigte Angabe")
    .required("Benötigte Angabe"),
  postalCode: Yup.string()
    .matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Benötigte Angabe")
    .required("Benötigte Angabe"),
});

const companionFields = [
  {
    name: "name",
    type: "text",
    label: "Name",
    autoComplete: "name",
    validation: Yup.string()
      .min(2, "Benötigte Angabe")
      .required("Benötigte Angabe"),
    autoFocus: true,
  },
  {
    name: "phoneNumber",
    type: "tel",
    label: "Mobilfunknummer",
    autoComplete: "tel",
  },
  {
    name: "streetAddress",
    type: "text",
    label: "Straße. Nr.",
    autoComplete: "street-address",
  },
  {
    name: "postalCode",
    type: "tel",
    label: "Postleitzahl",
    autoComplete: "postal-code",
  },
];

type Props = {
  handleSubmit?: Function, // includes the id as param
};

function TicketReservationAddCompanionForm({ handleSubmit }: Props) {
  const auth = useAuth();
  const firestore = useFirestore();
  const { enqueueSnackbar } = useSnackbar();

  const user = auth.currentUser;
  const companionsRef = firestore
    .collection("users")
    .doc(user.uid)
    .collection("companions");
  const companions = useFirestoreCollectionData(companionsRef);

  return (
    <Formik
      validationSchema={companionSchema}
      initialValues={{
        name: "",
        phoneNumber: "",
        streetAddress: "",
        postalCode: "",
      }}
      onSubmit={async (values) => {
        // check for duplicates
        const duplicate = _.find(companions, { name: values.name });
        if (duplicate != null) {
          enqueueSnackbar("Du hast diesen Namen bereits hinzugefügt.", {
            variant: "warning",
          });
          return;
        }

        // there are no duplicates
        const id = uuid();
        await companionsRef
          .doc(id)
          .set({ ...values, id }, {merge: true})
          .then(() => {
            if (handleSubmit != null) {
              handleSubmit(id);
            }
            // close modal and use callback
          })
          .catch((error) => {
            console.error(error);
            enqueueSnackbar(error.message || error, { variant: "error" });
          });
      }}
    >
      {({ submitForm, isSubmitting, resetForm }) => (
        <Form width="100%">
          {companionFields.map(({ name, type, label, ...other }) => (
            <Box mb={1} key={name}>
              <Field
                component={TextField}
                name={name}
                type={type}
                label={label}
                variant="filled"
                {...other}
              />
            </Box>
          ))}
          <Box mt={2} />
          {isSubmitting ? (
            <LinearProgress mb={1} />
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={isSubmitting}
              onClick={submitForm}
              type="submit"
            >
              Begleitung hinzufügen
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default TicketReservationAddCompanionForm;
